/**
 * HTTP wrapper for calling Cloud Functions.
 *
 * Gets the ID token from Firebase Auth (user signs in once via Google).
 * Token is refreshed automatically by Firebase SDK.
 *
 * @example
 * ```typescript
 * const state = await callRemoteFunction<SimulationState>('apiGetSimulationState', {
 *   appId: 'my-game',
 * })
 * ```
 *
 * ⚠️ PLAYGROUND-ONLY: This file depends on Firebase and should ONLY be imported by PlaygroundHost
 * and other playground-only modules. Do NOT import from barrel exports (http/index.ts).
 * This ensures Firebase is not bundled in production builds.
 */

import { getPlaygroundConfig, buildFunctionsBaseUrl, getCloudRunUrl, type PlaygroundConfig } from '../config/playground'
import { getFirebaseClient } from '../firebase/firebaseClient'
import { getPlaygroundGameSessionToken } from './playgroundGameSession'

/**
 * Type for a function that invokes a remote Cloud Function.
 */
export type RemoteFunctionInvoker = <TResponse>(
  functionName: string,
  payload?: unknown,
) => Promise<TResponse>

/**
 * Call a Cloud Function via HTTP POST with Bearer token authentication.
 *
 * @param functionName - Name of the Cloud Function to call (e.g., 'apiGetSimulationState')
 * @param payload - Optional JSON payload to send in the request body
 * @returns The JSON response from the function
 * @throws Error if not signed in or if the request fails
 */
export async function callRemoteFunction<TResponse>(
  functionName: string,
  payload?: unknown,
): Promise<TResponse> {
  return callBackendFunction(functionName, buildFunctionsBaseUrl, payload)
}

/**
 * Call a Cloud Run endpoint that preserves legacy Cloud Function-style names.
 *
 * Simulation endpoints are hosted by Cloud Run's H5 proxy, while rooms and
 * leaderboards still use Firebase Functions.
 */
export async function callCloudRunFunction<TResponse>(
  functionName: string,
  payload?: unknown,
): Promise<TResponse> {
  return callBackendFunction(functionName, getCloudRunUrl, payload)
}

/**
 * Shared playground RPC: authenticate with a Firebase ID token, POST the payload
 * to `${baseUrl}/${functionName}` with retries, and parse the JSON response.
 *
 * The only difference between Cloud Functions and Cloud Run calls is how the
 * base URL is resolved, so callers pass a resolver.
 */
async function callBackendFunction<TResponse>(
  functionName: string,
  resolveBaseUrl: (config: PlaygroundConfig) => string,
  payload?: unknown,
): Promise<TResponse> {
  const config = getPlaygroundConfig()

  if (!config) {
    throw new Error(
      '[RUN] Playground mode is not enabled. ' +
      'Make sure the Vite plugin is configured and you are running in dev mode.'
    )
  }

  const client = await getFirebaseClient()
  const idToken = await client.getIdTokenOrThrow()

  const baseUrl = resolveBaseUrl(config)
  const url = `${baseUrl}/${functionName}`

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`,
  }

  // Host-parity: attach the game-session token so enforce-mode H5 backend
  // surfaces (e.g. game-server:h5-leaderboard on /h5/simulation/recipe) accept
  // the request. Minting is lazy, uid-keyed, and fails safe — no token means no
  // header (the receiver's mode decides whether that blocks).
  const gst = await getPlaygroundGameSessionToken()
  if (gst) headers['x-h5-game-session'] = gst

  const response = await fetchWithRetry(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload ?? {}),
  }, config.rpcMaxRetries ?? 0, config.rpcRetryDelayMs ?? 1000)

  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, functionName)
    throw new Error(errorMessage)
  }

  const text = await response.text()
  try {
    return JSON.parse(text) as TResponse
  } catch {
    throw new Error(
      `[RUN] Backend call to ${functionName} returned invalid JSON: ${text.substring(0, 100)}`
    )
  }
}

/**
 * Fetch with retry logic for transient failures.
 */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries: number,
  retryDelayMs: number,
): Promise<Response> {
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, options)

      // Don't retry client errors (4xx), only server errors (5xx)
      if (response.ok || (response.status >= 400 && response.status < 500)) {
        return response
      }

      // Server error - might be transient, retry if we have attempts left
      if (attempt < maxRetries) {
        await delay(retryDelayMs)
        continue
      }

      return response
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))

      if (attempt < maxRetries) {
        await delay(retryDelayMs)
        continue
      }

      throw lastError
    }
  }

  throw lastError ?? new Error('[RUN] Request failed with no error details')
}

/**
 * Extract a meaningful error message from a failed response.
 */
async function extractErrorMessage(response: Response, functionName: string): Promise<string> {
  try {
    const errorBody = await response.json()
    const message = errorBody.error || errorBody.message || response.statusText
    return `[RUN] Backend call to ${functionName} failed (${response.status}): ${message}`
  } catch {
    return `[RUN] Backend call to ${functionName} failed (${response.status}): ${response.statusText}`
  }
}

/**
 * Simple delay helper.
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Create a remote function invoker with a custom implementation.
 * Useful for testing or custom authentication flows.
 */
export function createRemoteFunctionInvoker(
  invoke: RemoteFunctionInvoker = callRemoteFunction,
): RemoteFunctionInvoker {
  return invoke
}
